import React, { useState, useEffect } from 'react';
import { t } from '../lib/i18n';
import {
  listPhoneCalls, createPhoneCall, updatePhoneCall, listContractors,
  dialPhoneCall,
} from '../lib/api';
import { formatDate } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Phone, Plus, UserCheck, Calendar, FileText, PhoneCall, Loader2, PhoneOff } from 'lucide-react';
import { useAgentConfigGate } from '../components/common/JITAgentConfigModal';
import { getTwilioConfig } from '../lib/api';

export default function PhoneCalls({ lang, tenantId }) {
  const [calls, setCalls] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [notesDialog, setNotesDialog] = useState(null);
  const [form, setForm] = useState({ contractor_id: '', scheduled_at: '', purpose: '' });
  const [notesForm, setNotesForm] = useState({ notes: '', outcome: '', status: 'completed' });
  const [dialing, setDialing] = useState(null);  // T22: call_id being dialed
  const [twilioReady, setTwilioReady] = useState(null);  // T36: null=loading, true/false

  // T22 2026-05-27 — JIT phone config gate. Gdy agent.identity.phone == "",
  // pierwsze kliknięcie "Zadzwoń" otwiera modal pytający o numer Adama.
  const phoneGate = useAgentConfigGate('phone');

  // T36 2026-05-29 — sprawdź czy Twilio skonfigurowany. Jeśli NIE → "Zadzwoń"
  // wyszarzony z tooltipem (uczciwiej niż udawać że dzwoni).
  useEffect(() => {
    getTwilioConfig()
      .then(c => setTwilioReady(!!c.configured))
      .catch(() => setTwilioReady(false));
  }, []);

  async function actuallyDial(callId) {
    setDialing(callId);
    try {
      const r = await dialPhoneCall(callId);
      if (r.sid) {
        toast.success(`Połączenie inicjowane (Twilio SID: ${r.sid.slice(0, 8)}...)`);
      } else if (r.status === 'queued' || r.status === 'in_progress') {
        toast.success('Połączenie w kolejce u Twilio');
      } else {
        toast.info(`Status: ${r.status || 'unknown'}`);
      }
      loadData();
    } catch (e) {
      toast.error('Wybieranie nie powiodło się: ' + (e.response?.data?.detail || e.message));
    } finally {
      setDialing(null);
    }
  }

  function handleDial(callId) {
    if (phoneGate.needsConfig) {
      phoneGate.openConfig(() => actuallyDial(callId));
      return;
    }
    actuallyDial(callId);
  }

  useEffect(() => { loadData(); }, [tenantId]);

  async function loadData() {
    setLoading(true);
    try {
      const [c, ct] = await Promise.all([listPhoneCalls(tenantId), listContractors(tenantId)]);
      setCalls(c);
      setContractors(ct);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function handleCreate() {
    if (!form.contractor_id || !form.scheduled_at) {
      toast.error(lang === 'pl' ? 'Kontrahent i data s\u0105 wymagane' : 'Contractor and date are required');
      return;
    }
    try {
      await createPhoneCall(form, tenantId);
      toast.success(lang === 'pl' ? 'Rozmowa zaplanowana' : 'Call scheduled');
      setDialogOpen(false);
      setForm({ contractor_id: '', scheduled_at: '', purpose: '' });
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
  }

  async function handleUpdateCall(callId) {
    try {
      await updatePhoneCall(callId, notesForm);
      toast.success(lang === 'pl' ? 'Zaktualizowano' : 'Updated');
      setNotesDialog(null);
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
  }

  async function handleHandoff(callId) {
    try {
      await updatePhoneCall(callId, { handoff_to_human: true, handoff_reason: 'Client requested human contact' });
      toast.success(lang === 'pl' ? 'Przekazano cz\u0142owiekowi' : 'Handed off to human');
      loadData();
    } catch (err) {
      toast.error(lang === 'pl' ? 'B\u0142\u0105d' : 'Error');
    }
  }

  const statusColors = {
    scheduled: 'bg-blue-400/10 text-blue-400 border-blue-400/20',
    completed: 'bg-emerald-400/10 text-emerald-400 border-emerald-400/20',
    cancelled: 'bg-red-400/10 text-red-400 border-red-400/20'
  };

  return (
    <div className="space-y-6" data-testid="phone-calls-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk' }}>
            {t('phoneCalls', lang)}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {lang === 'pl' ? 'Zaplanowane rozmowy telefoniczne' : 'Scheduled phone calls'}
          </p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="gap-2" data-testid="schedule-call-button">
              <Plus className="w-4 h-4" />
              {t('scheduleCall', lang)}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('scheduleCall', lang)}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label className="text-xs">{t('selectContractor', lang)}</Label>
                <Select value={form.contractor_id} onValueChange={v => setForm(p => ({...p, contractor_id: v}))}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={t('selectContractor', lang)} />
                  </SelectTrigger>
                  <SelectContent>
                    {contractors.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">{t('scheduledAt', lang)}</Label>
                <Input
                  type="datetime-local"
                  value={form.scheduled_at}
                  onChange={e => setForm(p => ({...p, scheduled_at: e.target.value}))}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-xs">{t('purpose', lang)}</Label>
                <Input
                  value={form.purpose}
                  onChange={e => setForm(p => ({...p, purpose: e.target.value}))}
                  className="mt-1"
                  placeholder={lang === 'pl' ? 'Cel rozmowy...' : 'Call purpose...'}
                />
              </div>
              <Button onClick={handleCreate} className="w-full">{t('create', lang)}</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-20 w-full" />)}</div>
      ) : calls.length === 0 ? (
        <Card className="border border-border">
          <CardContent className="text-center py-12">
            <Phone className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">
              {lang === 'pl' ? 'Brak zaplanowanych rozm\u00f3w.' : 'No scheduled calls.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {calls.map(call => (
            <Card key={call.id} className="border border-border">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Phone className="w-4 h-4 text-primary" />
                      <h3 className="text-sm font-medium">{call.contractor_name || 'Unknown'}</h3>
                      <Badge variant="outline" className={`text-xs ${statusColors[call.status] || ''}`}>
                        {call.status}
                      </Badge>
                      {call.handoff_to_human && (
                        <Badge variant="outline" className="text-xs border-amber-400/30 text-amber-400 gap-1">
                          <UserCheck className="w-2.5 h-2.5" />
                          {t('handoffToHuman', lang)}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(call.scheduled_at)}
                    </p>
                    {call.purpose && <p className="text-xs text-muted-foreground mt-1">{call.purpose}</p>}
                    {call.notes && (
                      <p className="text-xs text-muted-foreground mt-1 p-2 rounded bg-muted/30">{call.notes}</p>
                    )}
                  </div>
                  <div className="flex gap-1 flex-shrink-0">
                    {call.status === 'scheduled' && (
                      <>
                        {/* T36 — gdy Twilio NIE skonfigurowany: button wyszarzony z uczciwym tooltipem */}
                        <Button
                          variant={twilioReady ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => twilioReady && handleDial(call.id)}
                          disabled={dialing === call.id || twilioReady === false}
                          className={`text-xs gap-1 ${twilioReady ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : 'opacity-60 cursor-not-allowed'}`}
                          data-testid={`dial-call-${call.id}`}
                          title={
                            twilioReady === false
                              ? 'Telefonia (Twilio) nie jest skonfigurowana — połączenia wychodzące niedostępne. Skonfiguruj TWILIO_* w ustawieniach serwera.'
                              : phoneGate.needsConfig
                                ? 'Najpierw skonfigurujesz numer telefonu Adama'
                                : 'Adam zadzwoni do kontrahenta przez Twilio'
                          }
                        >
                          {dialing === call.id
                            ? <Loader2 className="w-3 h-3 animate-spin" />
                            : twilioReady === false
                              ? <PhoneOff className="w-3 h-3" />
                              : <PhoneCall className="w-3 h-3" />}
                          {twilioReady === false
                            ? (lang === 'pl' ? 'Telefon offline' : 'Phone offline')
                            : (lang === 'pl' ? 'Zadzwoń' : 'Dial')}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNotesDialog(call.id);
                            setNotesForm({ notes: call.notes || '', outcome: '', status: 'completed' });
                          }}
                          className="text-xs gap-1"
                        >
                          <FileText className="w-3 h-3" />
                          {t('notes', lang)}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleHandoff(call.id)}
                          className="text-xs gap-1 text-amber-400"
                        >
                          <UserCheck className="w-3 h-3" />
                          {t('handoffToHuman', lang)}
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Notes Dialog */}
      <Dialog open={!!notesDialog} onOpenChange={() => setNotesDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{lang === 'pl' ? 'Notatki z rozmowy' : 'Call Notes'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <Label className="text-xs">{t('notes', lang)}</Label>
              <Textarea
                value={notesForm.notes}
                onChange={e => setNotesForm(p => ({...p, notes: e.target.value}))}
                className="mt-1 min-h-[80px]"
              />
            </div>
            <div>
              <Label className="text-xs">{t('outcome', lang)}</Label>
              <Input
                value={notesForm.outcome}
                onChange={e => setNotesForm(p => ({...p, outcome: e.target.value}))}
                className="mt-1"
              />
            </div>
            <Button onClick={() => handleUpdateCall(notesDialog)} className="w-full">{t('save', lang)}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* T22 — JIT phone config modal (renderowane gdy needsConfig + openConfig wywołane) */}
      <phoneGate.ConfigModal />
    </div>
  );
}
